import { Link } from '@tanstack/react-router';
import {
  Command, Keyboard, MessageSquare, BookOpen, Sparkles,
  ExternalLink, FileText, ShoppingBag, Receipt, Wallet,
} from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle, CardDescription } from '@/components/Card';
import { Button } from '@/components/Button';
import { Kbd } from '@/components/Kbd';
import { useUI } from '@/store/ui';

/**
 * /help — entry-point card grid with shortcut cheat-sheet, glossary of the
 * accounting terms that recur in the UI, and links to the API docs + repo.
 */
export function Help() {
  const togglePalette = useUI((s) => s.togglePalette);
  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Help' }]}
        title="Help"
        subtitle="Keyboard shortcuts, glossary, and where to go when something is missing."
      />
      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Command palette */}
          <Card>
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center shrink-0">
                <Command className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle>Command palette</CardTitle>
                <CardDescription>
                  The fastest way to jump anywhere. Open with <Kbd>⌘K</Kbd> (or <Kbd>Ctrl K</Kbd>),
                  type a document name or action, hit <Kbd>↵</Kbd>.
                </CardDescription>
                <Button className="mt-3" variant="secondary" onClick={togglePalette}>
                  <Sparkles className="size-4" /> Open palette
                </Button>
              </div>
            </div>
          </Card>

          {/* Keyboard shortcuts */}
          <Card>
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center shrink-0">
                <Keyboard className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle>Keyboard shortcuts</CardTitle>
                <ul className="mt-2 space-y-1.5 text-body-sm">
                  <ShortcutRow keys={['⌘', 'K']} label="Command palette" />
                  <ShortcutRow keys={['G', 'H']} label="Go to Home" />
                  <ShortcutRow keys={['⌘', 'S']} label="Save current draft" />
                  <ShortcutRow keys={['/']}      label="Focus search" />
                  <ShortcutRow keys={['?']}      label="This help page" />
                  <ShortcutRow keys={['Esc']}    label="Close dialogs / drop focus" />
                </ul>
              </div>
            </div>
          </Card>

          {/* Glossary */}
          <Card className="lg:col-span-2">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center shrink-0">
                <BookOpen className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle>Glossary — terms that recur in the UI</CardTitle>
                <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-body-sm">
                  <GlossaryRow term="Docstatus" body="0 = Draft (editable), 1 = Submitted (posted), 2 = Cancelled (reversed)." />
                  <GlossaryRow term="DN" body="Delivery Note — supplier's own document number for paper-trail matching." />
                  <GlossaryRow term="GRN" body="Goods Receipt Note (Purchase Receipt) — proves stock arrived." />
                  <GlossaryRow term="MR" body="Material Request — internal demand signal that becomes a PO." />
                  <GlossaryRow term="PO" body="Purchase Order — commitment to a supplier. Drives received-vs-billed status." />
                  <GlossaryRow term="PI" body="Purchase Invoice — supplier bill; settles against an open PO when linked." />
                  <GlossaryRow term="SI" body="Sales Invoice — customer bill; outstanding amount is settled by Payment Entries." />
                  <GlossaryRow term="SO" body="Sales Order — commitment from a customer before invoicing." />
                  <GlossaryRow term="JE" body="Journal Entry — manual GL posting. Debit = Credit invariant is enforced server-side." />
                  <GlossaryRow term="PE" body="Payment Entry — cash receive / pay / internal transfer that settles invoices and posts GL." />
                  <GlossaryRow term="PPh23" body="Indonesian withholding tax (income tax article 23). Recorded as a Payment Entry deduction." />
                  <GlossaryRow term="NPWP" body="Indonesian taxpayer number — 16 digits, optional on parties." />
                  <GlossaryRow term="PPN" body="Indonesian VAT (Pajak Pertambahan Nilai), usually 11%." />
                  <GlossaryRow term="PTKP" body="Personal tax-free threshold used in PPh21 payroll calculation." />
                  <GlossaryRow term="UOM" body="Unit of Measure (Unit, Pcs, Kg, Dozen). Derived units convert to a base UOM." />
                  <GlossaryRow term="BOM" body="Bill of Materials — recipe used by Work Orders to drive component stock movement." />
                </div>
              </div>
            </div>
          </Card>

          {/* Where to go next */}
          <Card>
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center shrink-0">
                <FileText className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle>API docs</CardTitle>
                <CardDescription>
                  Every doctype is fully reachable via the REST API. Use the API docs for bulk operations,
                  integrations, or features the UI doesn't expose yet.
                </CardDescription>
                <Button className="mt-3" variant="secondary" asChild>
                  <a href="/api/v1/docs" target="_blank" rel="noreferrer">
                    <ExternalLink className="size-4" /> Open API docs <Kbd>↗</Kbd>
                  </a>
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-start gap-3">
              <div className="size-10 rounded-md bg-accent-soft text-accent-hover inline-flex items-center justify-center shrink-0">
                <MessageSquare className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle>Common starting points</CardTitle>
                <ul className="mt-2 space-y-1.5 text-body-sm">
                  <li><Link to={'/accounting' as never} className="text-brand-green-ink hover:underline inline-flex items-center gap-1.5"><Wallet className="size-3.5" /> Finance module</Link></li>
                  <li><Link to={'/buying' as never} className="text-brand-green-ink hover:underline inline-flex items-center gap-1.5"><ShoppingBag className="size-3.5" /> Procurement workflow</Link></li>
                  <li><Link to={'/accounting/sales-invoices' as never} className="text-brand-green-ink hover:underline inline-flex items-center gap-1.5"><Receipt className="size-3.5" /> Sales Invoices list</Link></li>
                  <li><Link to={'/settings/appearance' as never} className="text-brand-green-ink hover:underline">⚙ Settings</Link></li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function ShortcutRow({ keys, label }: { keys: string[]; label: string }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-charcoal">{label}</span>
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            <Kbd>{k}</Kbd>
            {i < keys.length - 1 && <span className="text-stone">+</span>}
          </span>
        ))}
      </span>
    </li>
  );
}

function GlossaryRow({ term, body }: { term: string; body: string }) {
  return (
    <div>
      <span className="font-medium text-ink">{term}</span>
      <span className="text-stone"> — </span>
      <span className="text-charcoal">{body}</span>
    </div>
  );
}
